import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Notification, NotificationRealtimePayload, NotificationType, mapMailToNotification } from '@/types/notification.types';
import { RealtimeChannel } from '@supabase/supabase-js';
import { toast } from '@/hooks/useToast';
import { useI18n } from '@/hooks/useI18n';
import { PAYMENTS_DASHBOARD_QUERY_KEY } from '@/lib/payments/models/payments-dashboard.model';
import { PAYMENT_PLAN_QUERY_KEY } from '@/lib/builder/hooks/usePaymentPlan';

/** Riga grezza di public.user_notifications. */
type NotificationRow = {
  id: string;
  user_id: string;
  template: string | null;
  payload: any;
  message: string | null;
  redirect_link: string | null;
  read: boolean | null;
  created_at: string;
  updated_at: string;
};

/** Finestra temporale delle notifiche mostrate nel pannello. */
const NOTIFICATION_WINDOW_DAYS = 10;

/** Backoff per il ritentativo di sottoscrizione quando il channel cade. */
const RETRY_BASE_DELAY_MS = 1000;
const RETRY_MAX_DELAY_MS = 30000;

/**
 * Notifiche che dicono "lo stato di un pagamento e' cambiato".
 *
 * All'arrivo di una di queste i dati dei pagamenti gia' in cache sono vecchi, e non di
 * poco: con i metodi ad addebito differito — bonifico bancario in testa — fra il pagamento
 * del cliente e questa notifica passano giorni, quindi non c'e' nessun refetch spontaneo
 * ad avvicinarsi. Senza invalidazione il TD leggerebbe il toast "pagamento ricevuto" con
 * accanto una dashboard che continua a dire "da incassare" finche' non ricarica la pagina.
 */
const PAYMENT_STATUS_TEMPLATES = new Set([
  'notify_payment_received',
  'payment_failed',
  'payment_refunded',
  'payment_disputed',
  // Incasso registrato a mano da un TD o da un'agenzia: la rata passa a pagata e nasce una
  // commissione a debito. Per l'admin che sta guardando /payments e' un cambio di stato
  // come gli altri, e senza invalidare vedrebbe la rata ancora aperta.
  'manual_payment_platform_fee_due',
]);

type ToastVariant = 'default' | 'destructive' | 'success' | 'warning' | 'info';

const TOAST_VARIANT_BY_TYPE: Record<NotificationType, ToastVariant> = {
  info: 'info',
  success: 'success',
  warning: 'warning',
  error: 'destructive',
};

interface NotificationContextValue {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  /** true quando il canale realtime è attivo; false durante disconnessioni/riconnessioni. */
  isRealtimeConnected: boolean;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refetch: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider = ({ children }: NotificationProviderProps) => {
  const { t, language } = useI18n();
  /**
   * Le righe restano grezze in stato e vengono tradotte al render: così al
   * cambio lingua si ritraducono anche le notifiche già caricate.
   */
  const [rows, setRows] = useState<NotificationRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRealtimeConnected, setIsRealtimeConnected] = useState(false);

  // `t` cambia identità a ogni render: il ref evita di invalidare memo ed effetti.
  const tRef = useRef(t);
  tRef.current = t;

  // Stesso motivo del ref su `t`: l'effetto realtime non deve rimontarsi per questo.
  const queryClient = useQueryClient();
  const queryClientRef = useRef(queryClient);
  queryClientRef.current = queryClient;

  const channelRef = useRef<RealtimeChannel | null>(null);
  const currentUserIdRef = useRef<string | null>(null);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryAttemptRef = useRef(0);
  /**
   * Sequenza delle fetch in volo: una risposta lenta partita prima non deve
   * sovrascrivere lo stato prodotto da una fetch (o da un evento realtime) più recente.
   */
  const fetchSeqRef = useRef(0);

  // ---------------------------------------------------------------------------
  // Fetch
  // ---------------------------------------------------------------------------

  const fetchNotifications = useCallback(async (userId?: string | null) => {
    const resolvedUserId = userId ?? currentUserIdRef.current;
    const seq = ++fetchSeqRef.current;

    if (!resolvedUserId) {
      setRows([]);
      setIsLoading(false);
      return;
    }

    try {
      const since = new Date();
      since.setDate(since.getDate() - NOTIFICATION_WINDOW_DAYS);

      const { data, error } = await supabase
        .from('user_notifications')
        .select('*')
        .eq('user_id', resolvedUserId)
        .gte('created_at', since.toISOString())
        .order('created_at', { ascending: false });

      // Scartata se nel frattempo è partita un'altra fetch o è cambiato l'utente.
      if (seq !== fetchSeqRef.current || currentUserIdRef.current !== resolvedUserId) {
        return;
      }

      if (error) {
        console.error('[NotificationContext] fetch failed:', error);
        return;
      }

      setRows((data || []) as NotificationRow[]);
    } catch (error) {
      console.error('[NotificationContext] fetch threw:', error);
    } finally {
      if (seq === fetchSeqRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Realtime
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const clearRetry = () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };

    const removeChannel = async () => {
      clearRetry();
      if (channelRef.current) {
        const channel = channelRef.current;
        channelRef.current = null;
        await supabase.removeChannel(channel);
      }
      setIsRealtimeConnected(false);
    };

    const handleRealtimeEvent = (payload: NotificationRealtimePayload) => {
      if (payload.eventType === 'INSERT') {
        const row = payload.new as NotificationRow;
        setRows((prev) => {
          // L'evento può sovrapporsi al refetch di riconnessione: non duplicare.
          if (prev.some((r) => r.id === row.id)) return prev;
          return [row, ...prev];
        });

        // La notifica è l'unico segnale che il frontend riceve quando un incasso si
        // conclude: senza questo, la dashboard pagamenti e il piano rate a schermo
        // resterebbero fermi al momento in cui sono stati caricati.
        if (row.template && PAYMENT_STATUS_TEMPLATES.has(row.template)) {
          void queryClientRef.current.invalidateQueries({ queryKey: [PAYMENTS_DASHBOARD_QUERY_KEY] });
          void queryClientRef.current.invalidateQueries({ queryKey: [PAYMENT_PLAN_QUERY_KEY] });
        }

        // Toast solo per le notifiche non lette e solo se la tab è in primo piano:
        // niente consegna fuori dall'applicazione, e nessun toast accumulato da
        // mostrare al rientro (resta comunque il badge).
        if (!row.read && document.visibilityState === 'visible') {
          const notification = mapMailToNotification(row, tRef.current);
          toast({
            variant: TOAST_VARIANT_BY_TYPE[notification.type] ?? 'default',
            title: notification.title,
            description: notification.message,
          });
        }
        return;
      }

      if (payload.eventType === 'UPDATE') {
        const row = payload.new as NotificationRow;
        setRows((prev) => prev.map((r) => (r.id === row.id ? row : r)));
        return;
      }

      if (payload.eventType === 'DELETE' && payload.old) {
        setRows((prev) => prev.filter((r) => r.id !== payload.old?.id));
      }
    };

    const subscribeChannel = (userId: string) => {
      const channel = supabase
        .channel(`notifications-channel-${userId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'user_notifications',
            filter: `user_id=eq.${userId}`,
          },
          handleRealtimeEvent
        )
        .subscribe((status) => {
          // L'utente può essere cambiato mentre la subscribe era in volo.
          if (currentUserIdRef.current !== userId) return;

          if (status === 'SUBSCRIBED') {
            retryAttemptRef.current = 0;
            setIsRealtimeConnected(true);
            // Realtime non fa replay: gli eventi persi mentre eravamo
            // disconnessi si recuperano solo rileggendo la tabella.
            void fetchNotifications(userId);
            return;
          }

          if (
            status === 'CHANNEL_ERROR' ||
            status === 'TIMED_OUT' ||
            (status === 'CLOSED' && channelRef.current === channel)
          ) {
            setIsRealtimeConnected(false);
            scheduleRetry(userId);
          }
        });

      channelRef.current = channel;
    };

    const scheduleRetry = (userId: string) => {
      clearRetry();
      const delay = Math.min(
        RETRY_BASE_DELAY_MS * 2 ** retryAttemptRef.current,
        RETRY_MAX_DELAY_MS
      );
      retryAttemptRef.current += 1;

      retryTimeoutRef.current = setTimeout(() => {
        retryTimeoutRef.current = null;
        if (currentUserIdRef.current !== userId) return;
        void (async () => {
          await removeChannel();
          if (currentUserIdRef.current !== userId) return;
          subscribeChannel(userId);
        })();
      }, delay);
    };

    const setupForUser = async (userId: string | null) => {
      currentUserIdRef.current = userId; // prima di qualsiasi await, per evitare race
      retryAttemptRef.current = 0;
      await removeChannel();

      if (!userId) {
        fetchSeqRef.current++; // invalida eventuali fetch in volo
        setRows([]);
        setIsLoading(false);
        return;
      }

      await fetchNotifications(userId);
      if (currentUserIdRef.current !== userId) return;
      subscribeChannel(userId);
    };

    // Riallineamento al rientro in foreground: dopo sleep/sospensione della tab
    // la socket può essere caduta senza che arrivi un evento di errore.
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      const userId = currentUserIdRef.current;
      if (!userId) return;

      void fetchNotifications(userId);
      if (!channelRef.current) {
        subscribeChannel(userId);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      const nextUserId = session?.user?.id ?? null;
      // Ignora TOKEN_REFRESHED & co.: supabase-js propaga il nuovo JWT al
      // realtime da solo, ricreare il channel qui lo farebbe solo rimbalzare.
      if (nextUserId === currentUserIdRef.current) return;

      void setupForUser(nextUserId);
    });

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      subscription.unsubscribe();
      void removeChannel();
    };
  }, [fetchNotifications]);

  // ---------------------------------------------------------------------------
  // Azioni
  // ---------------------------------------------------------------------------

  // Traduzione derivata: ricalcolata quando cambiano le righe o la lingua attiva.
  const notifications = useMemo<Notification[]>(
    () => rows.map((row) => mapMailToNotification(row, tRef.current)),
    [rows, language]
  );

  const unreadCount = rows.filter((r) => !r.read).length;

  const markAsRead = useCallback(async (notificationId: string) => {
    // Ottimistico: il realtime confermerà comunque con l'evento UPDATE.
    setRows((prev) =>
      prev.map((r) => (r.id === notificationId ? { ...r, read: true } : r))
    );

    const { error } = await supabase
      .from('user_notifications')
      .update({ read: true })
      .eq('id', notificationId);

    if (error) {
      console.error('[NotificationContext] markAsRead failed:', error);
      setRows((prev) =>
        prev.map((r) => (r.id === notificationId ? { ...r, read: false } : r))
      );
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    const userId = currentUserIdRef.current;
    if (!userId) return;

    const previouslyUnread = rows.filter((r) => !r.read).map((r) => r.id);
    if (previouslyUnread.length === 0) return;

    setRows((prev) => prev.map((r) => ({ ...r, read: true })));

    const { error } = await supabase
      .from('user_notifications')
      .update({ read: true })
      .eq('user_id', userId)
      .eq('read', false);

    if (error) {
      console.error('[NotificationContext] markAllAsRead failed:', error);
      const unreadSet = new Set(previouslyUnread);
      setRows((prev) =>
        prev.map((r) => (unreadSet.has(r.id) ? { ...r, read: false } : r))
      );
    }
  }, [rows]);

  const refetch = useCallback(() => fetchNotifications(), [fetchNotifications]);

  const value: NotificationContextValue = {
    notifications,
    unreadCount,
    isLoading,
    isRealtimeConnected,
    markAsRead,
    markAllAsRead,
    refetch,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
